import React, { Suspense } from 'react'
import { Toaster } from '@shared-ui/toaster'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/components/NavigationTracker'
import { pagesConfig } from './pages.config'
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom'
import PageNotFound from '@/components/PageNotFound'
import { AuthProvider, useAuth } from '@/components/AuthContext'
import UserNotRegisteredError from '@/components/UserNotRegisteredError'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import Login from './pages/Login'

const { Pages, Layout, mainPage } = pagesConfig
const mainPageKey = mainPage ?? Object.keys(Pages)[0]
const MainPage = mainPageKey ? Pages[mainPageKey as keyof typeof Pages] : null

const LayoutWrapper = ({
  children,
  currentPageName,
}: {
  children: React.ReactNode
  currentPageName: string | undefined
}) =>
  Layout ? (
    <Layout currentPageName={currentPageName ?? ''}>{children}</Layout>
  ) : (
    <>{children}</>
  )

const LoadingSpinner = () => (
  <div className="flex h-full items-center justify-center p-8">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
)

function AppRoutes() {
  const { user } = useAuth()
  const role = user?.role || 'general_admin'
  const hiddenForAll = new Set(['Purchases', 'ZoomSettings'])
  const blockedForGeneral = new Set([
    'Donations',
    'PushNotifications',
    'AdminManagement',
  ])
  const allowedEntries = Object.entries(Pages).filter(
    ([path]) =>
      !hiddenForAll.has(path) &&
      (role === 'super_admin' ? true : !blockedForGeneral.has(path)),
  )
  const allowedPaths = new Set(allowedEntries.map(([path]) => path))

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route
          path="/"
          element={
            <LayoutWrapper
              currentPageName={
                allowedPaths.has(mainPageKey) ? mainPageKey : 'Dashboard'
              }
            >
              {allowedPaths.has(mainPageKey) && MainPage ? (
                <ErrorBoundary>
                  <MainPage />
                </ErrorBoundary>
              ) : (
                <Navigate to="/Dashboard" replace />
              )}
            </LayoutWrapper>
          }
        />
        {allowedEntries.map(([path, Page]) => (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                <ErrorBoundary>
                  <Page />
                </ErrorBoundary>
              </LayoutWrapper>
            }
          />
        ))}
        {role !== 'super_admin' &&
          Array.from(blockedForGeneral).map(path => (
            <Route
              key={path}
              path={`/${path}`}
              element={<Navigate to="/Dashboard" replace />}
            />
          ))}
        {Array.from(hiddenForAll).map(path => (
          <Route
            key={path}
            path={`/${path}`}
            element={<Navigate to="/Dashboard" replace />}
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  )
}

const ProtectedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } =
    useAuth()

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    )
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin()
      return null
    }
  }

  return (
    <CategoriesProvider>
      <AppRoutes />
    </CategoriesProvider>
  )
}

import { CategoriesProvider } from '@/components/CategoriesContext'

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <NavigationTracker />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/login"
                element={
                  <ErrorBoundary>
                    <Login />
                  </ErrorBoundary>
                }
              />
              <Route
                path="*"
                element={
                  <ErrorBoundary>
                    <ProtectedApp />
                  </ErrorBoundary>
                }
              />
              <Route path="/Login" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
          <Toaster />
        </Router>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
